import React, { useEffect, useState } from 'react';
import { Plus, FileStack, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import DecimalInput from '../components/DecimalInput';
import AutoGrowTextarea from '../components/AutoGrowTextarea';

const emptyLine = { description: '', isTextOnly: false, quantity: 1, unitPrice: 0, taxRate: 21 };
// Línea de solo texto: sin cantidad, sin precio y sin IVA.
const emptyTextLine = { description: '', isTextOnly: true, quantity: 0, unitPrice: 0, taxRate: 0 };

const InvoiceTemplates = ({ onApplyTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const modal = useModal();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await api.get('/invoice-templates');
      setTemplates(res.data);
    } catch (error) {
      modal.error('Error cargando plantillas');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setName('');
    setDescription('');
    setLines([{ ...emptyLine }]);
  };

  const addLine = () => {
    setLines([...lines, { ...emptyLine }]);
  };

  const addTextLine = () => {
    setLines([...lines, { ...emptyTextLine }]);
  };

  const updateLine = (index, key, value) => {
    const next = [...lines];
    next[index] = { ...next[index], [key]: value };
    setLines(next);
  };

  const removeLine = (index) => {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, idx) => idx !== index));
  };

  const createTemplate = async (event) => {
    event.preventDefault();
    try {
      const res = await api.post('/invoice-templates', { name, description, lines });
      setTemplates([...templates, res.data]);
      closeModal();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando plantilla');
    }
  };

  const removeTemplate = async (id) => {
    try {
      await api.delete(`/invoice-templates/${id}`);
      setTemplates(templates.filter((template) => Number(template.id) !== Number(id)));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error eliminando plantilla');
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <h2 className="card-title">Plantillas de factura</h2>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Nueva plantilla
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="empty-state" style={{ padding: 24 }}>
          <FileStack size={40} />
          <h3 style={{ fontSize: '1rem' }}>Cargando plantillas base</h3>
          <p>Se crearán automáticamente en este tenant</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {templates.map((template) => (
            <div key={template.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {template.name}
                    {template.isDefault && <span className="badge badge-success" style={{ marginLeft: 8 }}>Predeterminada</span>}
                  </div>
                  {template.description && <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{template.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => onApplyTemplate(template)}>
                    Usar plantilla
                  </button>
                  <button type="button" className="btn-icon" style={{ color: '#ef4444' }} onClick={() => removeTemplate(template.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2>Nueva plantilla</h2>
              <button type="button" className="btn-icon" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={createTemplate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" value={name} onChange={(event) => setName(event.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <input className="form-input" value={description} onChange={(event) => setDescription(event.target.value)} />
                </div>

                <h3 style={{ marginBottom: 8 }}>Líneas</h3>
                <div className="invoice-item-row invoice-item-row--header" aria-hidden="true">
                  <span>Descripción</span>
                  <span>Cant.</span>
                  <span>Precio unit.</span>
                  <span>IVA %</span>
                  <span></span>
                </div>
                {lines.map((line, index) => (
                  <div key={index} className="invoice-item-row invoice-item-row--editable invoice-item-row--template">
                    <div className="invoice-item-field invoice-item-field--full">
                      <span className="invoice-item-mobile-label">Descripción</span>
                      <AutoGrowTextarea
                        className="form-input invoice-item-concept"
                        placeholder={line.isTextOnly ? 'Texto libre, sin importe' : 'Descripción'}
                        value={line.description}
                        onChange={(event) => updateLine(index, 'description', event.target.value)}
                        required
                      />
                    </div>
                    {line.isTextOnly ? (
                      <div className="invoice-item-textonly-gap" aria-hidden="true" />
                    ) : (
                      <div className="invoice-item-compact-grid">
                        <div className="invoice-item-field">
                          <span className="invoice-item-mobile-label">Cant.</span>
                          <DecimalInput
                            value={line.quantity}
                            onValueChange={(value) => updateLine(index, 'quantity', value)}
                          />
                        </div>
                        <div className="invoice-item-field">
                          <span className="invoice-item-mobile-label">Precio unit.</span>
                          <DecimalInput
                            value={line.unitPrice}
                            onValueChange={(value) => updateLine(index, 'unitPrice', value)}
                          />
                        </div>
                        <div className="invoice-item-field">
                          <span className="invoice-item-mobile-label">IVA %</span>
                          <DecimalInput
                            value={line.taxRate}
                            onValueChange={(value) => updateLine(index, 'taxRate', value)}
                          />
                        </div>
                      </div>
                    )}
                    <div className="invoice-item-action">
                      <button type="button" className="btn-icon" style={{ color: '#ef4444' }} onClick={() => removeLine(index)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={addLine}>
                    <Plus size={16} /> Añadir línea
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={addTextLine}
                    title="Línea de solo texto: sin cantidad, sin precio y sin IVA."
                  >
                    <Plus size={16} /> Añadir línea de texto
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar plantilla</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceTemplates;
